/* =====================================================================
 * livePluginsPrefetch — Live 세션 시작 시 enabled plugin 목록 prefetch
 *
 * 동기
 * ----
 * Live Agent WS 는 ``ws.open → { type: 'auth', enabled_plugins }`` 로 시작
 * 하는데, 기존 구현은 ``ws.open`` 에서 ``listPlugins()`` 를 호출해 응답을
 * 기다렸다가 ``auth`` 를 보냈다. 이 HTTP 왕복(수백 ms ~ 수 초) 이 세션
 * 핸드셰이크의 serial critical path 에 걸려 "대화하기" 첫 인상을 느리게
 * 만든다.
 *
 * 해결
 * ----
 * 1) LiveAgentPage mount 시 ``prefetchLivePlugins()`` 를 selective-fire 로
 *    호출해, 실제 WS 가 열리기 전에 HTTP 응답을 미리 받아둔다.
 * 2) ``connectWs`` 에서는 ``getEnabledPluginIds(timeoutMs)`` 를 사용해
 *    캐시 hit 이면 즉시 반환, miss 이면 짧은 timeout 까지만 기다린 뒤
 *    빈 배열로 fallback. ``auth`` 전송 지연을 ~0ms 에 수렴시킨다.
 * 3) 결과는 작은 TTL 동안 재사용. TTL 내 중복 호출은 in-flight Promise 를
 *    공유해 네트워크 왕복을 합친다.
 *
 * 플러그인 연결 상태가 바뀌어도 TTL 만큼 지연이 생길 수 있지만, 라이브
 * 세션은 어차피 수 분 단위로 유지되며 다음 세션 시작 시 다시 fetch 한다.
 * ===================================================================== */

import { listPlugins, type PluginsListResponse } from '@auw/api-client';

/** Prefetch 결과 유효 시간. TTL 이내에는 캐시를 재사용. */
const CACHE_TTL_MS = 60_000;

/** ``connectWs.onopen`` 에서 auth 전에 기다릴 수 있는 최대 시간. */
const DEFAULT_AUTH_RACE_TIMEOUT_MS = 500;

interface CacheEntry {
  fetchedAt: number;
  data: PluginsListResponse;
}

let cache: CacheEntry | null = null;
let inflight: Promise<PluginsListResponse> | null = null;

function isFresh(entry: CacheEntry | null): entry is CacheEntry {
  return !!entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS;
}

function doFetch(): Promise<PluginsListResponse> {
  if (inflight) return inflight;
  inflight = listPlugins()
    .then((data) => {
      cache = { fetchedAt: Date.now(), data };
      return data;
    })
    .catch((err) => {
      // 실패는 캐시하지 않는다 — 다음 prefetch 가 재시도하도록.
      throw err;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * 백그라운드 prefetch. 에러는 삼키고 완료 여부만 반환한다.
 * LiveAgentPage mount / hover 같은 기회에 `void prefetchLivePlugins()` 로
 * 부담 없이 호출할 수 있다.
 */
export async function prefetchLivePlugins(): Promise<void> {
  if (isFresh(cache)) return;
  try {
    await doFetch();
  } catch {
    /* swallow */
  }
}

/** 캐시/prefetch 결과에서 연결된 플러그인 ID 목록을 추출. */
function extractConnectedIds(res: PluginsListResponse): string[] {
  return res.plugins.filter((p) => p.connected).map((p) => p.id);
}

/**
 * ``auth`` 전송 직전에 사용할 race getter.
 *
 *  - 캐시 신선 → 즉시 반환 (0ms)
 *  - 캐시 만료 / 없음 → 백그라운드 fetch 를 **트리거하되**, ``timeoutMs``
 *    안에 들어오지 않으면 빈 배열로 fallback
 *
 * fallback 이 발생해도 세션은 정상적으로 열리며, 연결된 플러그인 정보는
 * 이후 ``session_ready`` 응답의 ``connected_plugins`` 로 덮여쓰인다.
 */
export async function getEnabledPluginIds(
  timeoutMs: number = DEFAULT_AUTH_RACE_TIMEOUT_MS,
): Promise<string[]> {
  if (isFresh(cache)) return extractConnectedIds(cache.data);

  const fetchPromise = doFetch()
    .then(extractConnectedIds)
    .catch(() => [] as string[]);

  if (timeoutMs <= 0) return fetchPromise;

  return Promise.race<string[]>([
    fetchPromise,
    new Promise<string[]>((resolve) =>
      setTimeout(() => resolve([]), timeoutMs),
    ),
  ]);
}

/** 테스트/로그아웃 등에서 캐시를 수동으로 비울 때 사용. */
export function clearLivePluginsCache(): void {
  cache = null;
}
